import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const PAYMENT_METHOD_LABELS = {
  cash:         'Cash',
  card:         'Card',
  upi:          'UPI',
  netbanking:   'NetBanking / NEFT / RTGS / Bank Transfer',
  split:        'Cash + UPI / Card (Split)',
  splitUpiCard: 'UPI + Card (Split)',
  district:     'District by Zomato',
  snapmint:     'Snapmint',
};

const formatINR = (v) => {
  if (v === null || v === undefined) return '0';
  return Number(v).toLocaleString('en-IN');
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};

const istDate = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
const today = () => istDate().toISOString().slice(0, 10);
const startOfMonth = () => { const d = istDate(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
.sr-root { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.sr-root * { box-sizing: border-box; }
.sr-card { background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06); overflow: hidden; border: 1px solid rgba(0,0,0,0.04); }
.sr-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.sr-table thead th { background: #f8fafc; padding: 9px 10px; text-align: left; font-weight: 700; border-bottom: 2px solid #e2e8f0; font-size: 10px; text-transform: uppercase; color: #64748b; letter-spacing: 0.3px; white-space: nowrap; position: sticky; top: 0; z-index: 1; }
.sr-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; white-space: nowrap; }
.sr-table tbody tr:hover { background: #f0f7ff; }
.sr-table tbody tr.cancelled { opacity: 0.6; }
.sr-badge { display: inline-block; font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 10px; text-transform: uppercase; letter-spacing: 0.3px; }
.sr-badge-active { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; }
.sr-badge-cancelled { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
.sr-badge-unfulfilled { background: #fefce8; color: #854d0e; border: 1px solid #fde047; }
.sr-scroll::-webkit-scrollbar { height: 6px; }
.sr-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 3px; }
.sr-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
.sr-tab { padding: 6px 16px; border-radius: 8px; border: none; font-weight: 600; cursor: pointer; fontSize: 13px; transition: all 0.15s; }
.sr-tab-active { background: #4f46e5; color: #fff; }
.sr-tab-inactive { background: #f1f5f9; color: #64748b; }
.sr-tab-inactive:hover { background: #e2e8f0; }
`;

const S = {
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  title: { fontSize: 18, fontWeight: 800, color: '#4f46e5' },
  input: { padding: '5px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, color: '#334155' },
  select: { padding: '5px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, background: '#fff', color: '#334155', maxWidth: 140 },
  btn: { padding: '5px 14px', borderRadius: 8, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  btnPrimary: { background: '#4f46e5', color: '#fff' },
  btnGreen: { background: '#059669', color: '#fff' },
  loading: { textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 16 },
  empty: { textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 },
  count: { fontSize: 12, color: '#94a3b8', fontWeight: 500 },
};

// Order status shown in the report: a cancelled order is "cancelled" (a cancel also reverses the
// fulfillment, so fulfillment_status alone would wrongly read "unfulfilled"); otherwise fulfillment.
const orderStatusOf = (r) => (r.order_status === 'cancelled' ? 'cancelled' : (r.fulfillment_status || '-'));
const statusBadgeClass = (r) => (r.order_status === 'cancelled' ? 'cancelled' : (r.fulfillment_status === 'fulfilled' ? 'active' : 'unfulfilled'));

function Reports() {
  const [reportType, setReportType] = useState('sales');
  const [dateFrom, setDateFrom] = useState(startOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [locationId, setLocationId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [discountName, setDiscountName] = useState('');
  const [locations, setLocations] = useState([]);
  const [discountNames, setDiscountNames] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data.locations || [])).catch(() => {});
    api.get('/sales-report/discount-names').then(r => setDiscountNames(r.data.names || [])).catch(() => {});
    api.get('/sales-report/payment-methods').then(r => setPaymentMethods(r.data.methods || [])).catch(() => {});
  }, []);

  const fetchSalesReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { dateFrom, dateTo };
      if (locationId) params.locationId = locationId;
      if (paymentMethod) params.paymentMethod = paymentMethod;
      const r = await api.get('/sales-report', { params });
      setRows(r.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, locationId, paymentMethod]);

  const fetchDiscountReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { dateFrom, dateTo };
      if (locationId) params.locationId = locationId;
      if (paymentMethod) params.paymentMethod = paymentMethod;
      if (discountName) params.discountName = discountName;
      const r = await api.get('/sales-report/discount-report', { params });
      setRows(r.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, locationId, paymentMethod, discountName]);

  useEffect(() => {
    setRows([]);
    if (reportType === 'sales') fetchSalesReport();
    else fetchDiscountReport();
  }, [reportType, fetchSalesReport, fetchDiscountReport]);

  const exportSalesExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Sales Report');

    const cols = [
      { header: 'Date', key: 'date', width: 22 },
      { header: 'Store', key: 'store', width: 18 },
      { header: 'Order', key: 'order', width: 12 },
      { header: 'Product', key: 'product', width: 32 },
      { header: 'SKU', key: 'sku', width: 16 },
      { header: 'Category', key: 'category', width: 16 },
      { header: 'Qty', key: 'qty', width: 6 },
      { header: 'Unit Price', key: 'unitPrice', width: 12 },
      { header: 'Line Total', key: 'lineTotal', width: 12 },
      { header: 'Order Total', key: 'orderTotal', width: 12 },
      { header: 'Payment', key: 'payment', width: 12 },
      { header: 'Order Status', key: 'status', width: 18 },
      { header: 'Employee', key: 'employee', width: 16 },
      { header: 'Customer', key: 'customer', width: 20 },
      { header: 'City', key: 'city', width: 14 },
    ];
    ws.columns = cols;

    ws.getRow(1).eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      cell.alignment = { horizontal: 'center' };
    });

    rows.forEach(r => {
      ws.addRow({
        date: formatDate(r.created_at),
        store: r.location_name || '-',
        order: r.order_name,
        product: r.title || '-',
        sku: r.sku || '-',
        category: r.category || '-',
        qty: r.quantity,
        unitPrice: Number(r.unit_price),
        lineTotal: Number(r.line_total),
        orderTotal: Number(r.total_price),
        payment: r.payment_method,
        status: orderStatusOf(r),
        employee: r.employee_name || '-',
        customer: r.customer_name || '-',
        city: r.customer_city || '-',
      });
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `Sales_Report_${dateFrom}_to_${dateTo}.xlsx`);
  };

  const exportDiscountExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Discount Report');

    const cols = [
      { header: 'Date of Order', key: 'date', width: 22 },
      { header: 'Store Name', key: 'store', width: 18 },
      { header: 'Order ID', key: 'order', width: 12 },
      { header: 'Product Ordered', key: 'product', width: 32 },
      { header: 'SKU Ordered', key: 'sku', width: 16 },
      { header: 'Quantity Ordered', key: 'qty', width: 8 },
      { header: 'Category', key: 'category', width: 16 },
      { header: 'Price of Product', key: 'unitPrice', width: 14 },
      { header: 'Discount Value', key: 'discountValue', width: 14 },
      { header: 'Discount Name', key: 'discountName', width: 20 },
      { header: 'Price After Discount', key: 'priceAfterDiscount', width: 16 },
      { header: 'Total Order Price', key: 'totalOrderPrice', width: 14 },
      { header: 'Order Status', key: 'orderStatus', width: 16 },
      { header: 'Payment', key: 'payment', width: 14 },
      { header: 'Employee Name', key: 'employee', width: 16 },
    ];
    ws.columns = cols;

    ws.getRow(1).eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      cell.alignment = { horizontal: 'center' };
    });

    rows.forEach(r => {
      ws.addRow({
        date: formatDate(r.created_at),
        store: r.location_name || '-',
        order: r.order_name,
        product: r.title || '-',
        sku: r.sku || '-',
        qty: r.quantity,
        category: r.category || '-',
        unitPrice: Number(r.unit_price),
        discountValue: r.discount_value,
        discountName: r.discount_name || '-',
        priceAfterDiscount: r.price_after_discount,
        totalOrderPrice: r.total_order_price,
        orderStatus: orderStatusOf(r),
        payment: r.payment_method || '-',
        employee: r.employee_name || '-',
      });
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `Discount_Report_${dateFrom}_to_${dateTo}.xlsx`);
  };

  const handleExport = reportType === 'sales' ? exportSalesExcel : exportDiscountExcel;

  return (
    <div className="sr-root">
      <style>{CSS}</style>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={`sr-tab ${reportType === 'sales' ? 'sr-tab-active' : 'sr-tab-inactive'}`} onClick={() => setReportType('sales')}>Sales Report</button>
        <button className={`sr-tab ${reportType === 'discount' ? 'sr-tab-active' : 'sr-tab-inactive'}`} onClick={() => setReportType('discount')}>Discount Report</button>
      </div>

      <div style={S.topBar}>
        <div style={S.title}>{reportType === 'sales' ? 'Sales Report' : 'Discount Report'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={S.input} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={S.input} />
          </div>
          <select value={locationId} onChange={e => setLocationId(e.target.value)} style={S.select}>
            <option value="">All Stores</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={S.select}>
            <option value="">All Payments</option>
            <option value="splitUpiCard">UPI + Card (Split)</option>
            {paymentMethods.map(v => <option key={v} value={v}>{PAYMENT_METHOD_LABELS[v] || v}</option>)}
          </select>
          {reportType === 'discount' && (
            <select value={discountName} onChange={e => setDiscountName(e.target.value)} style={S.select}>
              <option value="">All Discounts</option>
              {discountNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          <button onClick={handleExport} disabled={!rows.length} style={{ ...S.btn, ...S.btnGreen, opacity: rows.length ? 1 : 0.5 }}>
            Export Excel
          </button>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {loading ? (
        <div style={S.loading}>Loading report...</div>
      ) : !rows.length ? (
        <div style={S.empty}>No orders found for the selected filters.</div>
      ) : (
        <>
          <div style={{ ...S.count, marginBottom: 8 }}>{rows.length} line items</div>
          <div className="sr-card">
            <div className="sr-scroll" style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
              {reportType === 'sales' ? (
                <table className="sr-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Store</th>
                      <th>Order</th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Category</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                      <th style={{ textAlign: 'right' }}>Unit Price</th>
                      <th style={{ textAlign: 'right' }}>Line Total</th>
                      <th style={{ textAlign: 'right' }}>Order Total</th>
                      <th>Payment</th>
                      <th>Order Status</th>
                      <th>Employee</th>
                      <th>Customer</th>
                      <th>City</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={r.order_status === 'cancelled' ? 'cancelled' : ''}>
                        <td>{formatDate(r.created_at)}</td>
                        <td>{r.location_name || '-'}</td>
                        <td style={{ fontWeight: 600 }}>{r.order_name}</td>
                        <td>{r.title || '-'}</td>
                        <td style={{ fontSize: 11, color: '#64748b' }}>{r.sku || '-'}</td>
                        <td>{r.category || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{r.quantity}</td>
                        <td style={{ textAlign: 'right' }}>{formatINR(r.unit_price)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatINR(r.line_total)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatINR(r.total_price)}</td>
                        <td>{r.payment_method}</td>
                        <td>
                          <span className={`sr-badge sr-badge-${statusBadgeClass(r)}`}>{orderStatusOf(r)}</span>
                        </td>
                        <td>{r.employee_name || '-'}</td>
                        <td>{r.customer_name || '-'}</td>
                        <td>{r.customer_city || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="sr-table">
                  <thead>
                    <tr>
                      <th>Date of Order</th>
                      <th>Store Name</th>
                      <th>Order ID</th>
                      <th>Product Ordered</th>
                      <th>SKU Ordered</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                      <th>Category</th>
                      <th style={{ textAlign: 'right' }}>Price of Product</th>
                      <th style={{ textAlign: 'right' }}>Discount Value</th>
                      <th>Discount Name</th>
                      <th style={{ textAlign: 'right' }}>Price After Discount</th>
                      <th style={{ textAlign: 'right' }}>Total Order Price</th>
                      <th>Order Status</th>
                      <th>Payment</th>
                      <th>Employee Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={r.order_status === 'cancelled' ? 'cancelled' : ''}>
                        <td>{formatDate(r.created_at)}</td>
                        <td>{r.location_name || '-'}</td>
                        <td style={{ fontWeight: 600 }}>{r.order_name}</td>
                        <td>{r.title || '-'}</td>
                        <td style={{ fontSize: 11, color: '#64748b' }}>{r.sku || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{r.quantity}</td>
                        <td>{r.category || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{formatINR(r.unit_price)}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>{formatINR(r.discount_value)}</td>
                        <td style={{ fontSize: 11 }}>{r.discount_name || '-'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatINR(r.price_after_discount)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatINR(r.total_order_price)}</td>
                        <td>
                          <span className={`sr-badge sr-badge-${statusBadgeClass(r)}`}>{orderStatusOf(r)}</span>
                        </td>
                        <td>{r.payment_method || '-'}</td>
                        <td>{r.employee_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Reports;
